import React, { ReactNode, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';

import { Configure, useCurrentRefinements, useHits } from 'react-instantsearch-hooks-web';
import { mapProduct } from 'helpers/algolia/map-product';
import { TILE_ATTRIBUTES } from 'helpers/algolia/tile-attributes';
import { useFormat } from 'helpers/hooks/useFormat';
import CurrentRefinements from './components/current-refinements';
import DesktopFacets from './components/desktop-facets';
import MobileFacets from './components/mobile-facets';
import SearchHeader from './components/search-header';
import { FacetConfiguration } from './types';
import ProductTile from '../product-tile';
import DesktopSortFacets from './components/desktop-facets/sort-facets';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import ViewToggle from '../product-list/components/view-toggle';
import ScrollToTopButton from 'components/commercetools-ui/atoms/scroll-to-top';
import ProductHits from './components/product-hits';

/**
 * BROWSER PATH — the /search results page, and the ONLY listing that still queries Algolia from the
 * browser. Everything here is driven by the typed query: results, facets and sort all come from
 * InstantSearch, which re-queries Algolia on every refinement because a search term is per-visitor and
 * cannot be preloaded or shared in a cache the way a listing can.
 *
 * Every OTHER listing is a V2 Contentstack block rendered by the server path
 * (cms-components-v2/section-with-product-listing → product-list-ssr), preloaded through the
 * Redis-cached backend action. The two paths share exactly two things and nothing else: the
 * <ProductTile> component, and TILE_ATTRIBUTES (helpers/algolia/tile-attributes) — the list of fields
 * a tile needs. They must NOT grow a shared context, store or filter helper again: this file used to
 * carry the slug/category listing rules as well, which is how one page's bundle ended up with both.
 */
/** TILE_ATTRIBUTES + the one field only /search reads (see the Configure below). */
const SEARCH_ATTRIBUTES = [...TILE_ATTRIBUTES, 'attributes.range'];

interface Props {
  noContentSuggestions?: any;
  searchQuery?: string;
  facetsConfiguration: Record<string, FacetConfiguration>;
  suggestions?: any;
}

interface NoResultProps {
  fallback: ReactNode;
}

function NoResultsBoundary({ children, fallback }: React.PropsWithChildren<NoResultProps>) {
  const { results } = useHits();
  if (results && !results?.__isArtificial && results?.nbHits === 0) {
    return (
      <>
        {fallback}
        <div hidden>{children}</div>
      </>
    );
  }
  return children;
}

function NoResults({ results, suggestions, searchQuery }: any) {
  const { formatMessage } = useFormat({ name: 'common' });
  const router = useRouter();

  const handleSuggestion = (suggestion: string) => {
    router.push(`/search?query=${encodeURIComponent(suggestion)}`);
  };

  return (
    <div>
      {searchQuery && results?.nbHits === 0 ? (
        <div className="m-auto max-w-[900px] text-center lg:!text-left">
          <p className={`results-zero no-result-container mb-2`}>No products found</p>
          <div className="d-inline">
            {formatMessage({
              id: 'no.resultfound.query',
              defaultMessage: 'No search results are found for your query:',
            })}
            <span className="font-bold"> &quot;{searchQuery ? searchQuery : ''}&quot;</span>.
            {suggestions.length > 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'did.you.mean',
                  defaultMessage: 'Did you mean?',
                })}{' '}
                {suggestions.map((suggestion: any, index: any) => (
                  <React.Fragment key={index}>
                    <span
                      role="button"
                      onClick={() => handleSuggestion(suggestion?.query)}
                      className="text-decoration-underline font-medium"
                    >
                      {suggestion?.query}
                    </span>
                    <span className="text-decoration-none">{index < suggestions.length - 1 && ', '}</span>
                  </React.Fragment>
                ))}
              </p>
            )}
            {suggestions.length === 0 && (
              <p className="d-inline">
                {' '}
                {formatMessage({
                  id: 'try.different.search',
                  defaultMessage: 'Please try a different search term.',
                })}
              </p>
            )}
          </div>
        </div>
      ) : (
        <p className={`${results?.nbHits === 0 && 'results-zero'} no-result-container`}>No products found</p>
      )}
    </div>
  );
}

const ProductListAlgolia: React.FC<Props> = ({
  searchQuery,
  facetsConfiguration,
  noContentSuggestions,
  suggestions,
}) => {
  const [isTabletSize] = useMediaQuery(desktop);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { locale } = useParams();

  const { results } = useHits();
  // The chips currently applied. A zero-hit search with refinements still shows the facet panel, so
  // the visitor can undo the refinement that emptied the results rather than being left with nothing.
  const { items } = useCurrentRefinements();

  const [currentView, setCurrentView] = useState<'grid' | 'list'>('grid');
  const handleViewChange = (view: 'grid' | 'list') => setCurrentView(view);

  const ConfigureSearch: any = Configure;

  return (
    <>
      <div id="plp-container" className="bg-white">
        <ConfigureSearch
          query={searchQuery ?? ''}
          hitsPerPage={12}
          facets={['*']}
          attributesToHighlight={[]} // Disable highlighting
          attributesToSnippet={[]} // Disable snippetting
          // The ONE tile attribute list, shared with the server path, plus the single extra field
          // THIS surface needs: `attributes.range`, which CustomStats reads to decide whether a
          // result points at a collection or a category. No facet attributes are added — unlike the
          // server path, /search asks Algolia for facet counts directly (`facets: ['*']`) rather
          // than computing them from the hits, so it does not have to retrieve the facet fields.
          attributesToRetrieve={SEARCH_ATTRIBUTES}
          maxValuesPerFacet={999}
        />
        <div className="mt-[1rem] px-24 lg:mt-[0.75rem] lg:px-48 xl:px-60 2xl:mt-[2rem] 2xl:px-80">
          <SearchHeader query={searchQuery ?? ''} noContentSuggestions={noContentSuggestions} />
        </div>

        <div
          className={`items-start gap-24 pt-24 md:flex md:flex-row lg:px-48 xl:px-60 2xl:gap-48 2xl:px-80 ${
            results?.nbHits === 0 && items?.length === 0 ? 'pb-0' : 'pb-48 xl:pb-0 2xl:pb-48'
          }`}
        >
          <div
            className={`${
              results?.nbHits === 0 && items?.length === 0 ? '' : 'lg:block'
            } hidden bg-white lg:mt-40  lg:min-w-[15rem]  2xl:min-w-[20rem]`}
          >
            <DesktopFacets facetsConfiguration={facetsConfiguration} />
          </div>

          <div className="w-full flex-1  bg-white lg:mt-40">
            <h1 className="px-24 py-12 text-32 font-medium leading-8 text-[#090909] md:py-32  md:px-28 md:text-40 lg:hidden lg:py-0  2xl:text-48">
              Products
            </h1>
            <div className="">
              <div id="product-suggestions" className="flex items-start pb-10 md:pb-24 lg:pb-0 xl:pb-0 2xl:pb-0">
                <CurrentRefinements />
                {isTabletSize && (
                  <NoResultsBoundary fallback={<span></span>}>
                    <div className="ml-auto flex flex-col items-end">
                      <DesktopSortFacets />
                    </div>
                  </NoResultsBoundary>
                )}
              </div>
            </div>

            <div
              className={`px-24 md:px-28 ${
                results?.nbHits === 0 && items?.length === 0 && 'hidden'
              } flex items-center justify-between gap-2 lg:block`}
            >
              <div className="block lg:hidden">
                <div className="cursor-pointer bg-transparent py-4 text-[14px]">
                  <ViewToggle onViewChange={handleViewChange} />
                </div>
              </div>

              <MobileFacets facetsConfiguration={facetsConfiguration} />
            </div>

            {isTabletSize ? (
              <NoResultsBoundary
                fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}
              >
                {currentView === 'list' && (
                  <div className="overflow-x-auto">
                    <div className="hidden text-[15px] font-semibold text-gray-800 lg:flex">
                      <div className="w-[60%] px-6 py-4 text-left">Product</div>
                      <div className="w-[20%] px-6 py-4 text-left">Code</div>
                      <div className="w-[20%] px-6 py-4 text-left">RRP</div>
                    </div>
                  </div>
                )}

                <ProductHits
                  loadingStyle="infinite"
                  hitComponent={({ hit }: any) => (
                    <ProductTile
                      product={mapProduct(hit, locale as string)}
                      isSearchResult
                      isCategoryPage
                      layout={currentView}
                    />
                  )}
                  rootClassName={currentView === 'grid' ? 'px-24 md:px-28 pt-32' : 'pt-32'}
                  listClassName={
                    currentView === 'grid'
                      ? 'grid grid-cols-1 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-16 [&>div]:aspect-[3/4]'
                      : 'flex flex-col'
                  }
                  loadMoreLabel={formatProductMessage({ id: 'show.more', defaultMessage: 'Show More' })}
                />
              </NoResultsBoundary>
            ) : (
              <NoResultsBoundary
                fallback={<NoResults results={results} suggestions={suggestions} searchQuery={searchQuery} />}
              >
                <ProductHits
                  loadingStyle="infinite"
                  hitComponent={({ hit }: any) => (
                    <ProductTile
                      product={mapProduct(hit, locale as string)}
                      isSearchResult
                      isCategoryPage
                      layout={currentView}
                    />
                  )}
                  rootClassName="px-24 md:px-28 pt-32"
                  listClassName={'grid md:grid-cols-2 xl:grid-cols-3 gap-16 '}
                  loadMoreLabel={formatProductMessage({ id: 'show.more', defaultMessage: 'Show More' })}
                />
              </NoResultsBoundary>
            )}
          </div>
        </div>
      </div>
      <ScrollToTopButton />
    </>
  );
};

export default ProductListAlgolia;
